<script setup lang="ts">
import type { CustomerReferenceTag } from '~/types'
import IconInfo from '~icons/mdi/InformationOutline'
import IconPercentCircle from '~icons/mdi/PercentCircleOutline'

const { $moment } = useNuxtApp()
const { hasCustomerReference } = useCustomerReferences()
const localePath = useLocalePath()
const isHybridSearch = useIsHybridSearch()

interface Props {
  listPrice: string | number
  currentPrice?: string | number
  hasScalePrices?: boolean
  scalePricing?: 'SUPPLIER' | 'GPO' | 'INDIVIDUAL' | 'NONE'
  reachedScalePrices: boolean
  campaign?: {
    beforePrice: string | number
    afterPrice: string | number
    discount: number
    discountPercent?: number
    discount_type: string
    name: string
    endDate: string
  }
  scalePrices?: Array<{ minQuantity: number, price: number | { amount: number, currency: string } }>
  selectedQuantity?: number
  availabilityStatus: string
  customerReference: Array<CustomerReferenceTag>
  humanMedicineRestricted?: boolean
  maxQuantity?: number
}

const props = withDefaults(defineProps<Props>(), {
  hasScalePrices: false,
  scalePricing: 'NONE',
})
const emits = defineEmits<{
  scalePricesHandler: [event: Event]
  selectScaleQuantity: [quantity: number]
}>()

const sortedScales = computed(() => {
  return props.scalePrices
    ? [...props.scalePrices]
        .filter(scale => !props.maxQuantity || scale.minQuantity <= props.maxQuantity)
        .sort((a, b) => a.minQuantity - b.minQuantity)
    : []
})

function scalePricesHandler(event: Event): void {
  emits('scalePricesHandler', event)
}

function getScaleBtnVariant(quantity: number) {
  if (!props.selectedQuantity || !sortedScales.value.length) {
    return 'text'
  }
  const applicableScale = sortedScales.value.findLast(scale => props.selectedQuantity! >= scale.minQuantity)

  return applicableScale && quantity === applicableScale.minQuantity ? 'tonal' : 'text'
}
</script>

<template>
  <v-row no-gutters align="center">
    <v-col
      v-if="props.humanMedicineRestricted"
      cols="12"
    >
      <v-btn
        component="NuxtLink"
        data-nav-id="product_ham_certificate_upload"
        variant="tonal"
        color="grey"
        class="text-body-2 font-weight-bold"
        :to="localePath('/account/profile')"
        data-test-id="ham-restriction-upload-btn"
      >
        {{ $t('searchResult.hamRestriction.uploadCertificate') }}
      </v-btn>
    </v-col>

    <v-col
      v-else
      cols="12"
      lg="6"
      md="12"
      sm="6"
    >
      <!-- 1. Campaign block -->
      <template v-if="props.campaign && (props.scalePricing !== 'INDIVIDUAL' && props.scalePricing !== 'GPO' || (props.currentPrice && props.campaign.afterPrice < props.currentPrice))">
        <div class="text-body-2 text-vm-grey-7 d-flex align-center">
          <span class="text-no-wrap">{{ $t('listPrice') }}:</span>
          <span class="text-vm-grey-9 text-decoration-line-through ml-1">
            {{ props.campaign.beforePrice }}
          </span>
          <span
            v-if="props.campaign.discount_type === 'list_price'"
            class="text-red-500 font-weight-bold ml-1"
          >
            (-{{ props.campaign.discount }}%)
          </span>
          <v-icon
            class="rounded-xl ml-2"
            density="compact"
            variant="text"
            color="vm-grey-7"
            :aria-label="$t('cart.sum.listPriceHint')"
          >
            <IconPercentCircle />
            <v-tooltip activator="parent" location="bottom">
              <div class="tooltip-text">
                {{ $t('searchResult.campaign.details', {
                  campaignName: props.campaign?.name,
                  campaignEndDate: $moment(props.campaign?.endDate).format($t('date.format')),
                }) }}
              </div>
            </v-tooltip>
          </v-icon>
        </div>
        <div class="mt-1 text-vm-grey-7">
          {{ $t('promoPrice') }}:
          <span class="text-vm-grey-9 font-weight-bold">
            {{ props.campaign.afterPrice }}
          </span>
          <span
            v-if="props.campaign.discount_type !== 'list_price'"
            class="text-red-500 font-weight-bold ml-1"
          >
            <!-- TODO: remove the campaign.discount after replacing base layer with old one  -->
            ({{ props.campaign.discount ?? props.campaign.discountPercent }}%)
          </span>
        </div>
        <div class="text-caption text-vm-grey-7 mt-1">
          {{ $t('promoDisclaimer') }}
        </div>
      </template>

      <!-- 2. Supplier scale price block -->
      <template v-else-if="props.scalePricing === 'SUPPLIER' && props.currentPrice && props.currentPrice !== props.listPrice">
        <div class="text-body-2 text-vm-grey-7 d-flex align-center">
          <span class="text-no-wrap">{{ $t('listPrice') }}</span>
          <span class="text-vm-grey-9 text-decoration-line-through ml-1">{{ props.listPrice }}</span>
          <v-icon
            class="rounded-xl ml-2"
            density="compact"
            variant="text"
            color="vm-grey-7"
            :aria-label="$t('cart.sum.listPriceHint')"
          >
            <IconInfo />
            <v-tooltip activator="parent" location="bottom">
              <div class="tooltip-text">
                {{ $t('cart.sum.listPriceHint') }}
              </div>
            </v-tooltip>
          </v-icon>
        </div>
        <div class="text-body-2 text-vm-grey-7 mt-1">
          {{ $t('searchResult.listPrice.supplierScale') }}
          <span class="font-weight-bold text-vm-grey-9">{{ props.currentPrice }}</span>
        </div>
      </template>

      <!-- 3. Special price block (INDIVIDUAL/GPO) -->
      <template v-else-if="props.currentPrice && props.scalePricing === 'INDIVIDUAL' || props.scalePricing === 'GPO'">
        <div class="text-body-2 text-vm-grey-7">
          <span class="text-no-wrap">{{ $t('listPrice') }}</span> <span class="text-vm-grey-9 text-decoration-line-through ml-1">{{ props.listPrice }}</span>
        </div>
        <div class="text-body-2 text-vm-grey-7 mt-1">
          {{ $t('yourPrice') }} <span class="font-weight-bold text-vm-grey-9 ml-1">{{ props.currentPrice }}</span>
        </div>
      </template>

      <!-- 4. Default price block -->
      <template v-else>
        <div class="d-flex align-center">
          <span class="text-body-2 text-vm-grey-7 text-no-wrap">{{ $t('listPrice') }}</span>
          <span class="font-weight-bold text-vm-grey-9 ml-1">{{ props.currentPrice }}</span>
          <v-icon
            class="rounded-xl ml-2"
            density="compact"
            variant="text"
            color="vm-grey-7"
            size="small"
            :aria-label="$t('cart.sum.listPriceHint')"
          >
            <IconInfo />
            <v-tooltip activator="parent" location="bottom">
              <div class="tooltip-text">
                {{ $t('cart.sum.listPriceHint') }}
              </div>
            </v-tooltip>
          </v-icon>
        </div>
      </template>
    </v-col>

    <!-- hybrid-search: new scale price shape -->
    <VmzProductScaleGroupHybrid
      v-if="isHybridSearch"
      :scale-prices="props.scalePrices"
      :campaign="props.campaign"
      :availability-status="props.availabilityStatus"
      :customer-reference="props.customerReference"
      :human-medicine-restricted="props.humanMedicineRestricted"
      :selected-quantity="props.selectedQuantity"
      :max-quantity="props.maxQuantity"
      @scale-prices-handler="emits('scalePricesHandler', $event)"
      @select-scale-quantity="emits('selectScaleQuantity', $event)"
    />

    <!-- legacy search: flat price (number) scale shape -->
    <v-col
      v-else-if="!props.humanMedicineRestricted && props.hasScalePrices && !props.campaign && props.availabilityStatus !== 'NOT_AVAILABLE'"
      cols="12"
      lg="6"
      md="12"
      sm="6"
      class="d-flex flex-column justify-start align-lg-end align-md-start align-sm-end mt-0 mt-md-3"
    >
      <v-btn
        :disabled="!hasCustomerReference(props.customerReference)"
        variant="text"
        class="text-vm-grey-7 d-flex justify-end mb-1 px-4 px-md-1 px-lg-4"
        density="compact"
        data-test-id="scale-prices-button"
        data-track-id="product_open_scale_price"
        @click="scalePricesHandler"
      >
        <span class="hide-scale-title">
          {{ $t('scaleDiscount.headline') }}
        </span>
        <template #append>
          <v-icon color="secondary" icon="veticon-tag" />
        </template>
      </v-btn>

      <!-- scale price buttons -->
      <section
        v-if="props.hasScalePrices && sortedScales.length > 0 && !props.campaign"
        class="d-flex ga-1 mr-lg-2 mr-md-0 mr-sm-2"
        data-test-id="scale-prices-items"
      >
        <v-btn
          v-for="(scale) in sortedScales.filter(s => s.minQuantity !== 1).slice(0, 4)"
          :key="scale.minQuantity"
          :disabled="!hasCustomerReference(props.customerReference)"
          size="24"
          class="text-body-2 text-vm-grey-9"
          :variant="getScaleBtnVariant(scale.minQuantity)"
          data-track-id="product_select_scale_quantity"
          @click="emits('selectScaleQuantity', scale.minQuantity)"
        >
          {{ scale.minQuantity }}
        </v-btn>
      </section>
    </v-col>
  </v-row>
</template>

<style scoped>
@media (min-width: 1280px) and (max-width: 1500px) {
  .hide-scale-title {
    display: none !important;
  }
}

.tooltip-text {
  max-width: 350px;
}
</style>
